import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Globe, Check } from "lucide-react";
import { cn } from "@/lib/utils";

interface ShareToExploreDialogProps {
  open: boolean;
  onClose: () => void;
  variations?: string[];
  initialSelectedUrls?: string[];
  initialNote?: string;
  onShare: (note: string, selectedUrls: string[]) => Promise<void>;
}

export const ShareToExploreDialog = ({
  open,
  onClose,
  variations = [],
  initialSelectedUrls,
  initialNote,
  onShare,
}: ShareToExploreDialogProps) => {
  const [note, setNote] = useState("");
  const [sharing, setSharing] = useState(false);
  const [selectedIndices, setSelectedIndices] = useState<number[]>([]);

  // Reset selection whenever the dialog opens or variations change.
  // If `initialSelectedUrls` is provided, pre-select those (intersect with current variations);
  // otherwise default to selecting all.
  useEffect(() => {
    if (open) {
      if (initialSelectedUrls && initialSelectedUrls.length > 0) {
        const preIdx = variations
          .map((url, i) => (initialSelectedUrls.includes(url) ? i : -1))
          .filter((i) => i !== -1);
        setSelectedIndices(preIdx.length > 0 ? preIdx : variations.map((_, i) => i));
      } else {
        setSelectedIndices(variations.map((_, i) => i));
      }
      setNote(initialNote ?? "");
    }
  }, [open, variations, initialSelectedUrls, initialNote]);

  const toggleIndex = (i: number) => {
    setSelectedIndices((prev) =>
      prev.includes(i) ? prev.filter((x) => x !== i) : [...prev, i].sort((a, b) => a - b),
    );
  };

  const handleShare = async () => {
    if (selectedIndices.length === 0) return;
    setSharing(true);
    try {
      const selectedUrls = selectedIndices.map((i) => variations[i]).filter(Boolean);
      await onShare(note.trim(), selectedUrls);
      setNote("");
      onClose();
    } finally {
      setSharing(false);
    }
  };

  const hasMultiple = variations.length > 1;
  const selectedCount = selectedIndices.length;
  const totalCount = variations.length;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5 text-primary" />
            Share to Explore
          </DialogTitle>
          <DialogDescription>
            {hasMultiple
              ? "Pick the variations you want to share with the community."
              : "Share your design with the community. Add an optional note about your design."}
          </DialogDescription>
        </DialogHeader>

        {hasMultiple && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium">Select variations</p>
              <p className="text-xs text-muted-foreground">
                {selectedCount} of {totalCount} selected
              </p>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {variations.map((url, i) => {
                const checked = selectedIndices.includes(i);
                return (
                  <button
                    type="button"
                    key={`${url}-${i}`}
                    onClick={() => toggleIndex(i)}
                    className={cn(
                      "relative overflow-hidden rounded-md border-2 transition-all aspect-square group",
                      checked ? "border-primary ring-2 ring-primary/30" : "border-border opacity-70 hover:opacity-100",
                    )}
                  >
                    <img src={url} alt={`Variation ${i + 1}`} className="w-full h-full object-cover" />
                    <div
                      className={cn(
                        "absolute top-1 right-1 h-5 w-5 rounded-sm flex items-center justify-center transition-colors",
                        checked ? "bg-primary text-primary-foreground" : "bg-background/80 border border-border",
                      )}
                    >
                      {checked && <Check className="h-3.5 w-3.5" />}
                    </div>
                    <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent px-1.5 py-1">
                      <p className="text-[10px] font-medium text-white">Variation {i + 1}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="space-y-3">
          <Textarea
            placeholder="Add your designer's thought... (optional, max 280 chars)"
            value={note}
            onChange={(e) => setNote(e.target.value.slice(0, 280))}
            className="min-h-[80px]"
          />
          <p className="text-xs text-muted-foreground text-right">{note.length}/280</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleShare} disabled={sharing || selectedCount === 0}>
            {sharing
              ? "Sharing..."
              : hasMultiple
              ? `Share ${selectedCount} selected`
              : "Share"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
